import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Sparkles, Download, Trash2 } from 'lucide-react';
import ScheduleGrid from '@/components/ScheduleGrid';
import StatsPanel, { Legend } from '@/components/StatsPanel';
import NvBalance from '@/components/NvBalance';
import WeekendBalance from '@/components/WeekendBalance';
import FeasibilityDialog from '@/components/FeasibilityDialog';
import TimelineView, { TimelineLegend } from '@/components/TimelineView';
import ScheduleSheet from '@/components/ScheduleSheet';
import { monthLabel, buildDays } from '@/lib/dates';
import { SHIFT_META } from '@/lib/constants';
import { LayoutGrid, GanttChartSquare, Rows3 } from 'lucide-react';

function nextMonth() {
  const d = new Date();
  let y = d.getFullYear();
  let m = d.getMonth() + 2; // ďalší mesiac (1-indexovaný)
  if (m > 12) { m -= 12; y += 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
}

function daysInMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

export default function SchedulePage({ workplace, employees }) {
  const [month, setMonth] = useState(nextMonth());
  const [allowOvertime, setAllowOvertime] = useState(true);
  const [allowFullday, setAllowFullday] = useState(false);
  const [fillGaps, setFillGaps] = useState(false);
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [saved, setSaved] = useState([]);
  const [feasReport, setFeasReport] = useState(null);
  const [feasOpen, setFeasOpen] = useState(false);

  const loadSaved = useCallback(async () => {
    if (!workplace) return;
    const res = await api.get('/schedules', { params: { workplace_id: workplace.id } });
    setSaved(res.data);
  }, [workplace]);

  useEffect(() => { loadSaved(); setSchedule(null); }, [loadSaved]);

  const runGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/schedules/generate', {
        workplace_id: workplace.id,
        start_date: `${month}-01`,
        num_days: daysInMonth(month),
        allow_overtime: allowOvertime,
        allow_fullday: allowFullday,
        fill_gaps: fillGaps,
        time_limit: 30,
      });
      setSchedule(res.data);
      loadSaved();
      const feas = res.data.stats?.feasibility;
      if (feas && !feas.feasible) {
        setFeasReport(feas);
        setFeasOpen(true);
      } else {
        toast.success(`Rozvrh vygenerovaný (${res.data.solver_status})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba pri generovaní');
    } finally {
      setLoading(false);
    }
  };

  const generate = runGenerate;

  const loadSchedule = async (sid) => {
    const res = await api.get(`/schedules/${sid}`);
    setSchedule(res.data);
  };

  const onCellChange = async (employee_id, dateIso, code) => {
    if (!schedule) return;
    const res = await api.put(`/schedules/${schedule.id}/assignment`, { employee_id, date: dateIso, code });
    setSchedule({ ...schedule, assignments: res.data.assignments, stats: res.data.stats });
  };

  const removeSchedule = async (sid) => {
    await api.delete(`/schedules/${sid}`);
    if (schedule?.id === sid) setSchedule(null);
    loadSaved();
    toast.success('Rozvrh zmazaný');
  };

  const gridEmployees = (schedule?.employees?.length ? schedule.employees : employees);
  const orderedEmployees = [...gridEmployees].sort(
    (a, b) => (a.role === 'brigadnik' ? 1 : 0) - (b.role === 'brigadnik' ? 1 : 0)
  );

  const exportCsv = () => {
    if (!schedule) return;
    const days = buildDays(schedule.start_date, schedule.num_days);
    const head = ['Zamestnanec', ...days.map((d) => d.slice(8))].join(';');
    const rows = orderedEmployees.map((e) =>
      [e.name, ...days.map((d) => SHIFT_META[(schedule.assignments[e.id] || {})[d] || 'X']?.short || '')].join(';')
    );
    const csv = [head, ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rozvrh_${schedule.start_date}.csv`;
    a.click();
  };

  if (!workplace) {
    return <Card className="p-8 text-center text-slate-500">Vyberte alebo vytvorte pracovisko.</Card>;
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs text-slate-500">Mesiac (plánuje sa celý mesiac)</Label>
            <Input data-testid="month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 mt-1" />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch data-testid="overtime-switch" checked={allowOvertime} onCheckedChange={setAllowOvertime} />
            <Label className="text-xs text-slate-600">Povoliť nadčas (do 48h/týž.)</Label>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch data-testid="fullday-switch" checked={allowFullday} onCheckedChange={setAllowFullday} />
            <Label className="text-xs text-slate-600">Preferovať celodenné zmeny (C 06:00–18:00)</Label>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch data-testid="fillgaps-switch" checked={fillGaps} onCheckedChange={setFillGaps} />
            <Label className="text-xs text-slate-600">Analýza podstavu (orientačne pridať navyše – nepoužíva sa v pláne)</Label>
          </div>
          <Button data-testid="generate-schedule-btn" onClick={generate} disabled={loading} className="bg-slate-900 hover:bg-slate-800 ml-auto">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generovať rozvrh
          </Button>
          {schedule && (
            <Button data-testid="export-csv-btn" variant="outline" onClick={exportCsv}>
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
        </div>
        {saved.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs text-slate-400">Uložené rozvrhy:</span>
            {saved.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full pl-3 pr-1 py-1">
                <button data-testid={`load-schedule-${s.id}`} onClick={() => loadSchedule(s.id)} className="hover:underline">{s.name}</button>
                <button data-testid={`del-schedule-${s.id}`} onClick={() => removeSchedule(s.id)} className="text-slate-400 hover:text-red-500 p-0.5"><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </Card>

      {schedule && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-heading font-semibold text-slate-900">{monthLabel(schedule.start_date)}</h2>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                <button data-testid="view-grid-btn" onClick={() => setView('grid')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${view === 'grid' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  <LayoutGrid className="w-3.5 h-3.5" /> Mriežka
                </button>
                <button data-testid="view-timeline-btn" onClick={() => setView('timeline')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${view === 'timeline' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  <GanttChartSquare className="w-3.5 h-3.5" /> Časová os
                </button>
                <button data-testid="view-sheet-btn" onClick={() => setView('sheet')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${view === 'sheet' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  <Rows3 className="w-3.5 h-3.5" /> Plachta
                </button>
              </div>
            </div>
            {view === 'timeline' ? <TimelineLegend /> : <Legend />}
          </div>
          {view === 'grid' && (
            <>
              <ScheduleGrid schedule={schedule} employees={orderedEmployees} workplace={workplace} onCellChange={onCellChange} />
              <p className="text-xs text-slate-400">Kliknutím na bunku upravíte zmenu. Červená bodka = porušenie týždenného odpočinku (§93).</p>
            </>
          )}
          {view === 'sheet' && (
            <>
              <ScheduleSheet schedule={schedule} employees={orderedEmployees} workplace={workplace} onCellChange={onCellChange} />
              <p className="text-xs text-slate-400">Plachta ako v TiDo: dátumy v riadkoch, zamestnanci v stĺpcoch. Kliknutím na bunku upravíte zmenu.</p>
            </>
          )}
          {view === 'timeline' && (
            <TimelineView schedule={schedule} employees={orderedEmployees} workplace={workplace} />
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 pt-2">
            <NvBalance stats={schedule.stats} />
            <WeekendBalance stats={schedule.stats} />
          </div>
          <StatsPanel stats={schedule.stats} />
          {schedule.stats?.extra_fund_hours > 0 && (
            <div data-testid="extra-fund-banner" className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-sm text-amber-800">
                <strong>Zistený podstav:</strong> chýba <strong>{schedule.stats.extra_fund_hours} h</strong> ≈ <strong>{schedule.stats.extra_people}</strong> úväzku/ov.
              </span>
            </div>
          )}
        </>
      )}
      {!schedule && (
        <Card className="p-12 text-center text-slate-400" data-testid="empty-schedule">
          <Sparkles className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          Nastavte obdobie a kliknite na „Generovať rozvrh".
        </Card>
      )}
      <FeasibilityDialog
        open={feasOpen}
        report={feasReport}
        onClose={() => setFeasOpen(false)}
      />
    </div>
  );
}
